import type { AnnotationsContext, MeasurementSummary } from './types';
import type { VtkAnnotation } from '../ViewportDre/Annotations/types';
import { AnnotationSource } from 'generated/graphql';
import {
  distanceBetweenPoints,
  findAngleBetweenThreePoints,
  getCobbAngleMeasurement,
} from '../ViewportDre/utils/math';

/** Sources to include in the context (UGC and GSPS, but not AI) */
const INCLUDED_SOURCES = new Set([AnnotationSource.Ugc, AnnotationSource.Gsps]);

/** Priority order for measurement types when truncating */
const MEASUREMENT_PRIORITY_TYPES = new Set([
  'LENGTH',
  'ANGLE',
  'COBB_ANGLE',
  'ELLIPSE',
  'CIRCLE',
  'RECT',
  'ARROW',
]);

/**
 * Convert a VtkAnnotation (from Recoil cache) to a lean MeasurementSummary.
 * Computes measurement values from segment geometry.
 */
export function vtkAnnotationToMeasurementSummary(ann: VtkAnnotation): MeasurementSummary | null {
  const summary: MeasurementSummary = {
    id: ann.id,
    type: ann.type || 'UNKNOWN',
    series_id: ann.seriesSmid ?? null,
  };

  // Extract metadata
  if (ann.metadata?.sopInstanceUID) {
    summary.sop_instance_uid = ann.metadata.sopInstanceUID;
  }
  if (ann.metadata?.frameNumber != null) {
    summary.frame_number = ann.metadata.frameNumber;
  }

  // Extract label and category
  if (ann.label) {
    summary.label = ann.label;
  }
  if (ann.category?.label) {
    summary.category_label = ann.category.label;
  }
  if (ann.category?.value) {
    summary.category_value = ann.category.value;
  }

  // Compute measurements from segments
  const type = ann.type;
  const segments = ann.segments;

  switch (type) {
    case 'LENGTH':
    case 'ARROW':
      // LENGTH/ARROW: segments[0] = [start, end]
      if (segments[0]) {
        summary.distance_mm = distanceBetweenPoints(segments[0][0], segments[0][1]);
      }
      break;

    case 'ANGLE':
      // ANGLE: segments = [[start, middle], [middle, end]]
      if (segments[0] && segments[1]) {
        summary.angle_degrees = findAngleBetweenThreePoints(
          segments[0][0], // start
          segments[0][1], // middle (vertex)
          segments[1][1] // end
        );
      }
      break;

    case 'COBB_ANGLE':
      // COBB_ANGLE: segments = [[start1, end1], [start2, end2]]
      if (segments[0] && segments[1]) {
        summary.cobb_angle_degrees = getCobbAngleMeasurement(
          segments[0][0],
          segments[0][1],
          segments[1][0],
          segments[1][1]
        );
      }
      break;

    case 'ELLIPSE':
    case 'CIRCLE':
    case 'RECT':
      // ROI stats (area, mean, stdDev, min, max) require image pixel data
      // and are computed dynamically at render time. We omit them in the
      // snapshot context since they're expensive to compute and not stored.
      break;

    case 'TEXT':
    case 'VERTEBRAE_LABEL':
    case 'LANDMARK':
    case 'CONTOUR':
    case 'POINT':
    case 'MULTI_POINT':
    case 'SEG_MASK_LABEL':
      // Label and category already captured above
      break;
  }

  return summary;
}

/**
 * Build an AnnotationsContext from Recoil state (VtkAnnotation array).
 *
 * This uses the same annotation data that the Viewer renders, ensuring
 * consistency with what the radiologist sees and avoiding stale cache issues.
 */
export function buildAnnotationsContextFromRecoilState({
  annotations,
  visibleSeriesIds,
  activeSeriesId,
  measurementLimit = 50,
}: {
  annotations: ReadonlyArray<VtkAnnotation>;
  visibleSeriesIds: ReadonlyArray<string>;
  activeSeriesId: string | null;
  measurementLimit?: number;
}): AnnotationsContext {
  const emptyContext: AnnotationsContext = {
    total_count: 0,
    by_type: {},
    measurements: [],
    truncated: false,
    measurement_limit: measurementLimit,
  };

  if (!annotations || annotations.length === 0) {
    return emptyContext;
  }

  const visibleSeriesSet = new Set(visibleSeriesIds);

  // Filter annotations by source (UGC and GSPS) and visible series
  const filteredAnnotations = annotations.filter((ann) => {
    // Filter by source: include UGC and GSPS, exclude AI
    if (!INCLUDED_SOURCES.has(ann.source)) {
      return false;
    }

    // Filter by visible series (annotations without seriesSmid pass through)
    if (ann.seriesSmid && !visibleSeriesSet.has(ann.seriesSmid)) {
      return false;
    }

    return true;
  });

  // Count by type
  const byType: Record<string, number> = {};
  for (const ann of filteredAnnotations) {
    const type = ann.type || 'UNKNOWN';
    byType[type] = (byType[type] || 0) + 1;
  }

  // Sort first, then truncate, then create summaries (more efficient)
  // Sort: active series first, then prioritize measurement types
  const sortedAnnotations = [...filteredAnnotations].sort((a, b) => {
    const aActive = a.seriesSmid === activeSeriesId ? 0 : 1;
    const bActive = b.seriesSmid === activeSeriesId ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;

    const aPriority = MEASUREMENT_PRIORITY_TYPES.has(a.type) ? 0 : 1;
    const bPriority = MEASUREMENT_PRIORITY_TYPES.has(b.type) ? 0 : 1;
    return aPriority - bPriority;
  });

  // Truncate first, then create summaries only for what we need
  const truncated = sortedAnnotations.length > measurementLimit;
  const truncatedAnnotations = sortedAnnotations.slice(0, measurementLimit);

  // Convert only the truncated set to measurement summaries
  const measurements = truncatedAnnotations
    .map((ann) => vtkAnnotationToMeasurementSummary(ann))
    .filter((m): m is MeasurementSummary => m !== null);

  return {
    total_count: filteredAnnotations.length,
    by_type: byType,
    measurements,
    truncated,
    measurement_limit: measurementLimit,
  };
}
